// Extract plain text from an uploaded resume file.
// Text-based PDFs: parsed client-side via pdfjs-dist.
// Image-based PDFs: pages rendered to images, sent to /api/ocr for AI-powered OCR.
// Also extracts candidate photo if present (cropped to 512x512).

import * as pdfjsLib from "pdfjs-dist";
import { buildAuthHeader } from "@/types";
import { KNOWN_SECTION_HEADERS, MAIN_SECTION_RE, SUB_SECTION_RE, PHONE_NUMBER_RE } from "./resumeSections";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

export interface ExtractionResult {
  text: string;
  photo: string | null; // base64 data-URL of 512x512 JPEG, or null
  fileBase64: string | null; // base64 (no data-URL prefix) of original file for R2 storage
  fileMime: string | null;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000; // 32 KB
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + chunk) as unknown as number[]
    );
  }
  return btoa(binary);
}

function inferMime(file: File): string {
  if (file.type) return file.type;
  const lower = file.name.toLowerCase();
  if (lower.endsWith(".pdf")) return "application/pdf";
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".webp")) return "image/webp";
  if (lower.endsWith(".txt")) return "text/plain";
  if (lower.endsWith(".md")) return "text/markdown";
  return "application/octet-stream";
}

export async function extractTextFromFile(
  file: File,
  onStatus?: (status: string) => void
): Promise<ExtractionResult> {
  const name = file.name.toLowerCase();

  // Cache the original bytes (≤8 MB cap to avoid blowing the JSON body limit).
  let fileBase64: string | null = null;
  let fileMime: string | null = null;
  if (file.size > 0 && file.size <= 8 * 1024 * 1024) {
    try {
      const buf = await file.arrayBuffer();
      fileBase64 = arrayBufferToBase64(buf);
      fileMime = inferMime(file);
    } catch {
      // ignore; we'll just skip the R2 upload
    }
  }

  if (name.endsWith(".pdf") || file.type === "application/pdf") {
    const r = await extractPdf(file, onStatus);
    return { ...r, fileBase64, fileMime };
  }
  if (
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    file.type.startsWith("text/")
  ) {
    return { text: await file.text(), photo: null, fileBase64, fileMime };
  }
  throw new Error(
    `Unsupported file type: ${file.type || name}. Upload a PDF or plain-text file.`
  );
}

async function extractPdf(
  file: File,
  onStatus?: (status: string) => void
): Promise<{ text: string; photo: string | null }> {
  const buf = await file.arrayBuffer();
  if (buf.byteLength === 0) {
    throw new Error("PDF file is empty (0 bytes).");
  }

  onStatus?.("Parsing PDF…");
  const data = new Uint8Array(buf);

  let doc: pdfjsLib.PDFDocumentProxy;
  try {
    doc = await pdfjsLib.getDocument({
      data: data.slice(0),
      useWorkerFetch: false,
      isEvalSupported: false,
      useSystemFonts: true,
    }).promise;
  } catch (e) {
    throw new Error(
      `Failed to parse PDF: ${e instanceof Error ? e.message : String(e)}`
    );
  }

  if (doc.numPages === 0) {
    throw new Error("PDF has no pages.");
  }

  // Render first page for photo extraction (and possibly OCR)
  onStatus?.("Rendering first page…");
  const firstPage = await renderPageWithDimensions(doc, 1);

  // Start photo extraction in parallel with text extraction
  const photoPromise = firstPage
    ? extractPhoto(firstPage.dataUrl, firstPage.width, firstPage.height, onStatus)
    : Promise.resolve(null);

  // Step 1: Try text extraction
  onStatus?.("Extracting text…");
  let textResult = await extractTextFromPages(doc);

  if (!textResult || textResult.length < 50) {
    // Step 2: Fall back to OCR
    onStatus?.("Image-based PDF detected. Running OCR…");
    const images = await renderPagesToImages(doc);
    if (images.length === 0) {
      throw new Error("Could not render PDF pages to images.");
    }
    onStatus?.(`Sending ${images.length} page(s) to AI for text recognition…`);
    const ocrText = await callOcrApi(images);
    if (!ocrText || ocrText.length < 20) {
      throw new Error(
        "OCR could not extract enough text from this PDF. Please try a clearer document."
      );
    }
    textResult = ocrText;
  }

  const photo = await photoPromise;

  return { text: textResult, photo };
}

async function extractPhoto(
  pageImage: string,
  pageWidth: number,
  pageHeight: number,
  onStatus?: (status: string) => void
): Promise<string | null> {
  try {
    onStatus?.("Detecting candidate photo…");
    const res = await fetch("/api/extract-photo", {
      method: "POST",
      headers: { "content-type": "application/json", ...buildAuthHeader() },
      body: JSON.stringify({
        image: pageImage,
        page_width: pageWidth,
        page_height: pageHeight,
      }),
    });

    if (!res.ok) return null;
    const box = (await res.json()) as {
      found: boolean;
      x: number;
      y: number;
      w: number;
      h: number;
    };
    if (!box.found) return null;

    onStatus?.("Cropping candidate photo…");
    return await cropPhoto(pageImage, box);
  } catch {
    return null;
  }
}

async function cropPhoto(
  pageImage: string,
  box: { x: number; y: number; w: number; h: number }
): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Pixel coordinates from Claude — clamp to image bounds
      let sx = Math.max(0, box.x);
      let sy = Math.max(0, box.y);
      let sw = Math.min(box.w, img.width - sx);
      let sh = Math.min(box.h, img.height - sy);

      // Sanity check
      if (sw < 10 || sh < 10) {
        resolve(null);
        return;
      }

      // Make it square — center-crop the longer side
      const side = Math.min(sw, sh);
      const cx = sx + (sw - side) / 2;
      const cy = sy + (sh - side) / 2;

      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }

      ctx.drawImage(img, cx, cy, side, side, 0, 0, 512, 512);

      // Verify the crop isn't blank (all white/transparent)
      const sample = ctx.getImageData(128, 128, 256, 256).data;
      let nonWhitePixels = 0;
      for (let i = 0; i < sample.length; i += 4) {
        if (sample[i] < 240 || sample[i + 1] < 240 || sample[i + 2] < 240) {
          nonWhitePixels++;
        }
      }
      // If less than 5% of sampled pixels are non-white, it's blank
      if (nonWhitePixels < (256 * 256) * 0.05) {
        resolve(null);
        return;
      }

      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(null);
    img.src = pageImage;
  });
}

interface PageRender {
  dataUrl: string;
  width: number;
  height: number;
}

async function renderPageWithDimensions(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNum: number
): Promise<PageRender | null> {
  try {
    const page = await doc.getPage(pageNum);
    const scale = 1.5;
    const MAX_DIM = 1600;
    let viewport = page.getViewport({ scale });

    if (viewport.width > MAX_DIM || viewport.height > MAX_DIM) {
      const ratio = Math.min(MAX_DIM / viewport.width, MAX_DIM / viewport.height);
      viewport = page.getViewport({ scale: scale * ratio });
    }

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    await page.render({ canvasContext: ctx, viewport }).promise;
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.7),
      width: canvas.width,
      height: canvas.height,
    };
  } catch {
    return null;
  }
}

async function renderPageToImage(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNum: number
): Promise<string | null> {
  const result = await renderPageWithDimensions(doc, pageNum);
  return result?.dataUrl ?? null;
}

interface TextItem { str: string; x: number; y: number; width: number; fontSize: number; }

// Group items into visual lines by Y proximity, then sort each line left-to-right.
function groupIntoLines(items: TextItem[], yTolerance = 4): TextItem[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => b.y - a.y);
  const groups: TextItem[][] = [[sorted[0]]];
  for (let i = 1; i < sorted.length; i++) {
    const last = groups[groups.length - 1];
    if (Math.abs(sorted[i].y - last[0].y) <= yTolerance) {
      last.push(sorted[i]);
    } else {
      groups.push([sorted[i]]);
    }
  }
  for (const g of groups) g.sort((a, b) => a.x - b.x);
  return groups;
}

// Join text items with gap-aware concatenation.
// PDFs sometimes store a single word as multiple adjacent text items (e.g. "Man"+"ila").
// When the gap between item[i].end and item[i+1].start is < 1.5 pt they are
// visually touching → concatenate directly; otherwise insert a space.
function joinItems(items: TextItem[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0].str;
  const sorted = [...items].sort((a, b) => a.x - b.x);
  let out = sorted[0].str;
  for (let i = 1; i < sorted.length; i++) {
    const gap = sorted[i].x - (sorted[i - 1].x + sorted[i - 1].width);
    out += gap < 1.5 ? sorted[i].str : " " + sorted[i].str;
  }
  return out;
}

// When minColGapPts is provided, splits a visual line into multiple text lines
// wherever the physical gap between consecutive items exceeds the threshold.
// This handles mixed-layout pages where items from two columns land on the same
// Y-band but have a large horizontal gap between them.
// Adjacent segments that look like a word split across the column boundary
// (prev ends with a letter, next starts with lowercase) are joined directly.
function linesToText(lineGroups: TextItem[][], minColGapPts?: number): string {
  return lineGroups
    .map(g => {
      if (!minColGapPts || g.length <= 1) {
        return joinItems(g).trim();
      }
      const sorted = [...g].sort((a, b) => a.x - b.x);
      const segs: TextItem[][] = [[sorted[0]]];
      let prevEnd = sorted[0].x + sorted[0].width;
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].x - prevEnd > minColGapPts) segs.push([]);
        segs[segs.length - 1].push(sorted[i]);
        prevEnd = sorted[i].x + sorted[i].width;
      }
      const segTexts = segs.map(s => joinItems(s).trim()).filter(Boolean);
      // Detect word fragments split across the column gap and join them:
      // if prev segment ends with a letter and cur starts with lowercase → concatenate.
      const out: string[] = [];
      for (const seg of segTexts) {
        if (out.length > 0 && /[a-zA-Z]$/.test(out[out.length - 1]) && /^[a-z]/.test(seg)) {
          out[out.length - 1] += seg;
        } else {
          out.push(seg);
        }
      }
      return out.join("\n");
    })
    .filter(Boolean)
    .join("\n");
}

// ── Column boundary detection ──────────────────────────────────────────────────

const BINS = 40;
const MIN_COL_GAP_PT = 15;
const Y_BUCKET_PT = 4;

function buildXHistogram(items: TextItem[], pageWidth: number): number[] {
  const bw = pageWidth / BINS;
  const counts = new Array(BINS).fill(0);
  for (const it of items) counts[Math.min(Math.floor(it.x / bw), BINS - 1)]++;
  return counts;
}

function isGenuineGutter(items: TextItem[], boundary: number): boolean {
  const leftItems = items.filter(it => it.x < boundary);
  const rightItems = items.filter(it => it.x >= boundary);
  const leftByBucket = new Map<number, TextItem[]>();
  for (const it of leftItems) {
    const b = Math.round(it.y / Y_BUCKET_PT);
    if (!leftByBucket.has(b)) leftByBucket.set(b, []);
    leftByBucket.get(b)!.push(it);
  }
  let shared = 0;
  let narrow = 0;
  for (const [bucket, lits] of leftByBucket) {
    const rits = rightItems.filter(it => Math.abs(Math.round(it.y / Y_BUCKET_PT) - bucket) <= 1);
    if (!rits.length) continue;
    shared++;
    const leftEnd = Math.max(...lits.map(it => it.x + (it.width || 0)));
    const rightStart = Math.min(...rits.map(it => it.x));
    if (rightStart - leftEnd < MIN_COL_GAP_PT) narrow++;
  }
  return !(shared >= 3 && narrow > shared * 0.5);
}

function candidateBoundaries(counts: number[], avg: number, bw: number, items: TextItem[]): number[] {
  const lo = Math.floor(BINS * 0.25);
  const hi = Math.ceil(BINS * 0.75);
  const boundaries: number[] = [];
  for (let b = lo; b <= hi; b++) {
    if (counts[b] > avg * 0.3) continue;
    const boundary = (b + 0.5) * bw;
    const leftFrac = items.filter(it => it.x < boundary).length / items.length;
    if (leftFrac < 0.10 || leftFrac > 0.90) continue;
    if (!isGenuineGutter(items, boundary)) continue;
    boundaries.push(boundary);
  }
  return boundaries;
}

// Returns 0–2 column boundaries. Callers choose 1-/2-/3-column path by length.
function findColumnBoundaries(items: TextItem[], pageWidth: number): number[] {
  if (items.length < 10) return [];
  const bw = pageWidth / BINS;
  const counts = buildXHistogram(items, pageWidth);
  const avg = items.length / BINS;
  let all = candidateBoundaries(counts, avg, bw, items);
  if (all.length === 0) {
    // Retry on middle 50% by Y (handles full-width headers masking the body gap)
    const sorted = [...items].sort((a, b) => a.y - b.y);
    const mid = sorted.slice(Math.floor(sorted.length * 0.25), Math.ceil(sorted.length * 0.75));
    const midCounts = buildXHistogram(mid, pageWidth);
    const midAvg = mid.length / BINS;
    all = candidateBoundaries(midCounts, midAvg, bw, mid);
  }
  // Return top-2 most balanced splits (fewest item count difference between columns)
  const scored = all
    .map(b => {
      const L = items.filter(it => it.x < b).length;
      const R = items.length - L;
      return { b, diff: Math.abs(L - R) };
    })
    .sort((a, b2) => a.diff - b2.diff)
    .map(s => s.b)
    .slice(0, 2);
  return scored.sort((a, b) => a - b);
}

// ── Section band detection ─────────────────────────────────────────────────────

function computeMedianFontSize(items: TextItem[]): number {
  const sizes = items.map(it => it.fontSize).filter(s => s > 0).sort((a, b) => a - b);
  if (!sizes.length) return 10;
  const mid = Math.floor(sizes.length / 2);
  return sizes.length % 2 === 0 ? (sizes[mid - 1] + sizes[mid]) / 2 : sizes[mid];
}

function computeMedianLineGap(lineGroups: TextItem[][]): number {
  if (lineGroups.length < 2) return 12;
  const gaps: number[] = [];
  for (let i = 1; i < lineGroups.length; i++) {
    gaps.push(Math.abs(lineGroups[i - 1][0].y - lineGroups[i][0].y));
  }
  gaps.sort((a, b) => a - b);
  const mid = Math.floor(gaps.length / 2);
  return gaps.length % 2 === 0 ? (gaps[mid - 1] + gaps[mid]) / 2 : gaps[mid];
}

function lineMatchesKnown(text: string, knownHeaders: string[]): boolean {
  const lower = text.toLowerCase().trim();
  if (MAIN_SECTION_RE.test(text.trim()) || SUB_SECTION_RE.test(text.trim())) return true;
  return knownHeaders.some(h => h.toLowerCase() === lower);
}

function qualifiesAsHeader(
  lineItems: TextItem[],
  lineText: string,
  medianFontSize: number,
  yGapAbove: number,
  medianLineGap: number,
  knownHeaders: string[]
): boolean {
  const maxFs = Math.max(...lineItems.map(it => it.fontSize));
  const fontSizeStrong = maxFs > medianFontSize * 1.25;
  const knownPatternStrong = lineMatchesKnown(lineText, knownHeaders);
  if (fontSizeStrong || knownPatternStrong) return true;
  const upper = (lineText.match(/[A-Z]/g) ?? []).length;
  const allCaps = lineText.length <= 60 && upper >= 3 && !/[a-z]/.test(lineText);
  const yGap = yGapAbove > medianLineGap * 1.5;
  return allCaps && yGap;
}

function detectSectionBands(items: TextItem[], knownHeaders: string[]): TextItem[][] {
  const lineGroups = groupIntoLines(items);
  if (lineGroups.length < 2) return [items];
  const medianFontSize = computeMedianFontSize(items);
  const medianLineGap = computeMedianLineGap(lineGroups);
  const headerYs: number[] = [];
  for (let i = 0; i < lineGroups.length; i++) {
    const group = lineGroups[i];
    const lineText = joinItems(group).trim();
    if (!lineText || PHONE_NUMBER_RE.test(lineText)) continue;
    const prevY = i > 0 ? lineGroups[i - 1][0].y : group[0].y;
    const yGapAbove = Math.abs(prevY - group[0].y);
    if (qualifiesAsHeader(group, lineText, medianFontSize, yGapAbove, medianLineGap, knownHeaders)) {
      headerYs.push(group[0].y);
    }
  }
  if (headerYs.length === 0) return [items];
  // Sort descending Y (PDF: higher Y = higher on page)
  const sorted = [...headerYs].sort((a, b) => b - a);
  const bands: TextItem[][] = [];
  for (let i = 0; i < sorted.length; i++) {
    const topY = sorted[i];
    const bottomY = i + 1 < sorted.length ? sorted[i + 1] : -Infinity;
    const band = items.filter(it => it.y <= topY && it.y > bottomY);
    if (band.length > 0) bands.push(band);
  }
  return bands.length > 0 ? bands : [items];
}

// ── Content-type classification ────────────────────────────────────────────────

const BULLET_CHARS = /^[•●◦✓✔►➢➤→✗✖\-*]/;

function classifyContentType(items: TextItem[]): "bulleted" | "stacked" | "paragraph" {
  const lines = groupIntoLines(items).map(g => joinItems(g).trim()).filter(Boolean);
  if (lines.length === 0) return "paragraph";
  const bulletLines = lines.filter(l => BULLET_CHARS.test(l)).length;
  if (bulletLines / lines.length >= 0.25) return "bulleted";
  const avgLen = lines.reduce((s, l) => s + l.length, 0) / lines.length;
  if (avgLen < 40 && lines.length >= 4) return "stacked";
  return "paragraph";
}

// ── Per-band two-/three-column extraction ─────────────────────────────────────

function nearTopY(arr: TextItem[]): number {
  if (!arr.length) return 0;
  const ys = arr.map(it => it.y).sort((a, b) => b - a);
  return ys[Math.min(Math.max(1, Math.floor(arr.length * 0.10)), ys.length - 1)];
}

function extractTwoColumnBand(band: TextItem[], boundary: number): string {
  const leftItems = band.filter(it => it.x + it.width / 2 < boundary);
  const rightItems = band.filter(it => it.x + it.width / 2 >= boundary);
  const maxYLeft = nearTopY(leftItems);
  const maxYRight = nearTopY(rightItems);
  const PAIRED_TOP_THRESHOLD = 40;
  const Y_BKT = 4;
  const leftBkts = new Set(leftItems.map(it => Math.round(it.y / Y_BKT)));
  const rightBkts = new Set(rightItems.map(it => Math.round(it.y / Y_BKT)));
  let shared = 0;
  for (const b of leftBkts) {
    if (rightBkts.has(b) || rightBkts.has(b + 1) || rightBkts.has(b - 1)) shared++;
  }
  const total = new Set([...leftBkts, ...rightBkts]).size;
  const pairedRatio = total > 0 ? shared / total : 0;
  if (Math.abs(maxYLeft - maxYRight) < PAIRED_TOP_THRESHOLD && pairedRatio >= 0.30) {
    return linesToText(groupIntoLines([...leftItems, ...rightItems]), 20);
  }
  const [primary, secondary] =
    maxYLeft >= maxYRight
      ? [groupIntoLines(leftItems), groupIntoLines(rightItems)]
      : [groupIntoLines(rightItems), groupIntoLines(leftItems)];
  return `${linesToText(primary)}\n\n${linesToText(secondary)}`;
}

function extractThreeColumnBand(band: TextItem[], b1: number, b2: number): string {
  const left = band.filter(it => it.x + it.width / 2 < b1);
  const mid = band.filter(it => it.x + it.width / 2 >= b1 && it.x + it.width / 2 < b2);
  const right = band.filter(it => it.x + it.width / 2 >= b2);
  const yL = nearTopY(left);
  const yM = nearTopY(mid);
  const yR = nearTopY(right);
  if (Math.abs(yL - yM) < 40 && Math.abs(yM - yR) < 40) {
    return linesToText(groupIntoLines([...left, ...mid, ...right]), 20);
  }
  const cols = [{ items: left, top: yL }, { items: mid, top: yM }, { items: right, top: yR }]
    .filter(c => c.items.length > 0)
    .sort((a, b) => b.top - a.top);
  return cols.map(c => linesToText(groupIntoLines(c.items))).join("\n\n");
}

function extractBandText(band: TextItem[], pageWidth: number): string {
  if (band.length < 15) return linesToText(groupIntoLines(band), 20);
  const ct = classifyContentType(band);
  const boundaries = findColumnBoundaries(band, pageWidth);
  if (boundaries.length === 0) {
    const gap = ct === "stacked" ? 8 : 20;
    return linesToText(groupIntoLines(band), gap);
  }
  if (boundaries.length === 1) return extractTwoColumnBand(band, boundaries[0]);
  return extractThreeColumnBand(band, boundaries[0], boundaries[1]);
}

// ── Known headers fetch (once per session) ────────────────────────────────────

let _knownHeadersCache: string[] | null = null;

async function fetchKnownHeaders(): Promise<string[]> {
  if (_knownHeadersCache !== null) return _knownHeadersCache;
  try {
    const res = await fetch("/api/cv/section/headers", { headers: buildAuthHeader() });
    if (!res.ok) { _knownHeadersCache = []; return []; }
    const data = (await res.json()) as { headers: Array<{ name: string }> };
    _knownHeadersCache = data.headers.map(h => h.name);
  } catch {
    _knownHeadersCache = [];
  }
  return _knownHeadersCache;
}

async function extractTextFromPages(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<string> {
  const dbHeaders = await fetchKnownHeaders();
  const allKnownHeaders = [...new Set([...KNOWN_SECTION_HEADERS, ...dbHeaders])];
  const parts: string[] = [];

  for (let i = 1; i <= doc.numPages; i++) {
    try {
      const page = await doc.getPage(i);
      const [content, viewport] = await Promise.all([
        page.getTextContent(),
        Promise.resolve(page.getViewport({ scale: 1 })),
      ]);
      const pageWidth = viewport.width;

      const items: TextItem[] = [];
      for (const item of content.items) {
        if (!("str" in item) || typeof item.str !== "string" || !item.str.trim()) continue;
        const t = (item as any).transform;
        if (!Array.isArray(t) || t.length < 6) continue;
        // Skip text rotated more than ~45° (e.g. sideways date banners in two-column CVs).
        // For horizontal text |scaleX (t[0])| dominates; for ~90° rotated text |skewY (t[1])| dominates.
        if (Math.abs(t[1]) > Math.abs(t[0])) continue;
        const fontSize = Math.abs(t[0]);
        items.push({ str: item.str, x: t[4], y: t[5], width: (item as any).width ?? 0, fontSize });
      }
      if (items.length === 0) continue;

      const bands = detectSectionBands(items, allKnownHeaders);
      const bandTexts = bands.map(band => extractBandText(band, pageWidth)).filter(Boolean);
      const pageText = bandTexts.join("\n\n");

      if (pageText) parts.push(pageText);
    } catch {
      // Skip pages that fail
    }
  }

  return parts
    .join("\n\n")
    .replace(/[ \t]+/g, " ")
    .replace(/ \n/g, "\n")
    .replace(/^[ \t]*(?:page\s+)?\d{1,3}[ \t]*$/gim, "")
    .replace(/([^\n])(\s*[•●◦✓✔►➢➤→✗✖](?:\s|$))/g, "$1\n$2")
    .replace(/([^\n])(\s*\d+[.)]\s+)(?=[A-Z])/g, "$1\n$2")
    .replace(/(?<!\n)\n([ \t]*[A-Z][A-Z\s&/\\-]{2,58}[A-Z][ \t]*)\n/g, "\n\n$1\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function renderPagesToImages(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<string[]> {
  const images: string[] = [];
  const maxPages = Math.min(doc.numPages, 10);

  for (let i = 1; i <= maxPages; i++) {
    const img = await renderPageToImage(doc, i);
    if (img) images.push(img);
  }

  return images;
}

async function callOcrApi(images: string[]): Promise<string> {
  const res = await fetch("/api/ocr", {
    method: "POST",
    headers: { "content-type": "application/json", ...buildAuthHeader() },
    body: JSON.stringify({ images }),
  });

  if (!res.ok) {
    let detail = "";
    try {
      const err = await res.json();
      detail = (err as any).error || JSON.stringify(err);
    } catch {
      detail = await res.text();
    }
    throw new Error(`OCR failed: ${detail}`);
  }

  const data = (await res.json()) as { text: string };
  return data.text;
}
